import { useHistoricoDeCargas } from '../../../api/queries/chamados'
import { Explicacao } from '../../../components/Explicacao'
import { AvisoDeErro, Esqueleto } from '../../../components/Estados'
import { Cartao } from '../../../components/ListaDeDados'
import { cn } from '../../../lib/cn'
import { dataHora } from '../../../lib/datas'
import { ROTULO_SITUACAO_DA_CARGA, TEXTOS } from '../../../lib/glossario'
import type { OcorrenciaDaCarga, RegistroDeImportacao } from '../../../types/chamado'
import { competenciaPorExtenso } from '../competencia'

/**
 * As cargas já feitas, da mais recente para a mais antiga.
 *
 * Inclui as que falharam, e é o ponto: uma carga que morre no meio precisa deixar rastro, senão o
 * histórico diz "concluída" para tudo e ninguém descobre por que faltam chamados num mês. O
 * `sha256` aparece encurtado porque é o que explica um "já importado" — dois arquivos com nomes
 * diferentes e a mesma impressão digital são o mesmo arquivo.
 *
 * @returns o cartão do histórico de cargas
 */
export function HistoricoDeCargas() {
  const { data: cargas, isPending, isError, error } = useHistoricoDeCargas()

  return (
    <Cartao
      titulo={TEXTOS.importacao.historico}
      descricao="Cada envio de planilha, com o que entrou e o que ficou de fora."
      acao={<Explicacao termo="cargaDaPlanilha" />}
    >
      {isPending && <Esqueleto linhas={4} />}
      {isError && <AvisoDeErro erro={error} titulo="Não foi possível carregar o histórico" />}
      {cargas && cargas.length === 0 && <p className="text-sm text-slate-600">{TEXTOS.importacao.semHistorico}</p>}
      {cargas && cargas.length > 0 && (
        <ul className="space-y-3">
          {cargas.map((carga) => (
            <LinhaDaCarga key={carga.id} carga={carga} />
          ))}
        </ul>
      )}
    </Cartao>
  )
}

const COR_DA_SITUACAO: Record<string, string> = {
  EM_ANDAMENTO: 'bg-amber-50 text-amber-900 ring-amber-200',
  CONCLUIDA: 'bg-green-50 text-green-900 ring-green-200',
  FALHOU: 'bg-red-50 text-red-900 ring-red-200',
}

/** Uma carga do histórico, com os contadores que a resumem e o erro quando ela falhou. */
function LinhaDaCarga({ carga }: { carga: RegistroDeImportacao }) {
  return (
    <li className="rounded-lg border border-slate-200 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 break-words font-medium text-slate-900">{carga.arquivo}</span>
        <span
          className={cn(
            'whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset',
            COR_DA_SITUACAO[carga.situacao] ?? 'bg-slate-100 text-slate-700 ring-slate-200',
          )}
        >
          {ROTULO_SITUACAO_DA_CARGA[carga.situacao] ?? carga.situacao}
        </span>
      </div>

      <p className="mt-1 text-xs text-slate-600">
        {carga.competencia && <>{competenciaPorExtenso(carga.competencia)} · </>}
        {dataHora(carga.iniciadoEm)}
        {carga.importadoPorNome && <> · por {carga.importadoPorNome}</>}
        {/* A impressão digital só existe em carga de ARQUIVO. A rodada automática do e-Niterói não
            tem arquivo para resumir, e a idempotência dela vem do número do chamado — escrever
            "conteúdo —" ali sugeriria um hash que ninguém pode conferir. */}
        {carga.sha256 && (
          <>
            {' '}
            · conteúdo <span className="font-mono">{carga.sha256.slice(0, 12)}</span>
          </>
        )}
      </p>

      <p className="mt-1 text-xs text-slate-700">
        {carga.linhasLidas} lidas · {carga.novos} novos · {carga.alterados} atualizados ·{' '}
        <span className={carga.recusados > 0 ? 'font-semibold text-amber-800' : undefined}>
          {carga.recusados} recusadas
        </span>
      </p>

      {carga.erro && <p className="mt-1 max-w-prose text-xs text-red-800">{carga.erro}</p>}

      <Ocorrencias titulo={TEXTOS.importacao.avisos} ocorrencias={carga.avisos} tom="aviso" />
      <Ocorrencias titulo={TEXTOS.importacao.recusas} ocorrencias={carga.recusas} tom="recusa" />
    </li>
  )
}

/**
 * O que a carga achou, dobrado até alguém querer ver.
 *
 * **Dobrado, e não escondido.** A contagem fica visível na linha da carga; o texto abre com um
 * clique. A rodada automática produz os mesmos avisos toda vez que a causa continua existindo — se
 * todos viessem abertos, o histórico viraria uma parede de texto repetido e ninguém leria nenhum.
 *
 * @param props o título, as ocorrências e o tom
 * @returns o bloco dobrável, ou nada quando não há ocorrência
 */
function Ocorrencias({
  titulo,
  ocorrencias,
  tom,
}: {
  titulo: string
  ocorrencias: OcorrenciaDaCarga[]
  tom: 'aviso' | 'recusa'
}) {
  if (ocorrencias.length === 0) {
    return null
  }
  return (
    <details className="mt-2">
      <summary
        className={cn(
          'cursor-pointer text-xs font-medium',
          tom === 'aviso' ? 'text-amber-800' : 'text-red-800',
        )}
      >
        {ocorrencias.length} {titulo.toLowerCase()}
      </summary>
      <ul className="mt-1.5 space-y-1.5">
        {ocorrencias.map((ocorrencia, indice) => (
          <li
            key={`${ocorrencia.referencia}-${indice}`}
            className={cn(
              'max-w-prose rounded-md border px-2.5 py-1.5 text-xs',
              tom === 'aviso' ? 'border-amber-200 bg-amber-50' : 'border-red-200 bg-red-50',
            )}
          >
            {ocorrencia.referencia && <span className="font-medium text-slate-800">{ocorrencia.referencia}: </span>}
            <span className="text-slate-700">{ocorrencia.motivo}</span>
          </li>
        ))}
      </ul>
    </details>
  )
}
